import React, { useState } from 'react';

const WorkflowBuilder = () => {
  const [selectedNode, setSelectedNode] = useState('condition');
  const [stepName, setStepName] = useState('Is User Premium?');

  const stepLibrary = {
    triggers: [
      { id: 'webhook', name: 'Webhook', icon: '🔗' },
      { id: 'schedule', name: 'Schedule', icon: '⏰' }
    ],
    actions: [
      { id: 'ai-task', name: 'AI Agent Task', icon: '🤖' },
      { id: 'send-email', name: 'Send Email', icon: '📧' },
      { id: 'update-db', name: 'Update DB', icon: '🗄️' }
    ],
    logic: [
      { id: 'condition', name: 'Condition (If/Else)', icon: '🔀' },
      { id: 'parallel', name: 'Parallel Branch', icon: '🔄' }
    ]
  };

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-white overflow-hidden h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-b-[#233c48] px-6 py-3 bg-background-light dark:bg-background-dark shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 text-white">
            <div className="text-primary">
              <svg fill="none" height="24" viewBox="0 0 48 48" width="24" xmlns="http://www.w3.org/2000/svg">
                <path d="M44 4H30.6666V17.3334H17.3334V30.6666H4V44H44V4Z" fill="currentColor"></path>
              </svg>
            </div>
            <h2 className="text-white text-lg font-bold leading-tight tracking-[-0.015em] hidden md:block">OrbitMind Workflow Builder</h2>
          </div>
          <div className="h-8 w-[1px] bg-[#233c48] mx-2"></div>
          <div className="flex items-center gap-2">
            <span className="text-[#92b7c9] text-sm font-medium">Model:</span>
            <select className="form-input flex min-w-48 resize-none overflow-hidden rounded-lg text-white focus:outline-0 focus:ring-0 border border-[#325567] bg-[#192b33] focus:border-primary h-9 text-sm font-normal px-2">
              <option value="one">Support Agent v2 (GPT-4o)</option>
              <option value="two">Creative Writer (Claude 3.5)</option>
              <option value="three">Data Analyst Pro</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <button className="flex min-w-[84px] cursor-pointer items-center justify-center rounded-lg h-9 px-4 bg-[#233c48] text-white text-sm font-bold hover:bg-[#2d4d5c] transition-colors">
              <span>Save Draft</span>
            </button>
            <button className="flex min-w-[84px] cursor-pointer items-center justify-center rounded-lg h-9 px-4 bg-primary text-white text-sm font-bold hover:bg-opacity-90 transition-colors">
              <span>Publish</span>
            </button>
          </div>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-xs border border-[#233c48]">
            AD
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <main className="flex flex-1 overflow-hidden">
        {/* Left Sidebar: Steps Library */}
        <aside className="w-64 border-r border-[#233c48] bg-background-light dark:bg-background-dark flex flex-col shrink-0">
          <div className="p-4 border-b border-[#233c48]">
            <h1 className="text-white text-sm font-bold uppercase tracking-wider">Steps Library</h1>
            <p className="text-[#92b7c9] text-xs mt-1">Drag elements to the canvas</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Triggers */}
            <div>
              <h3 className="text-[#92b7c9] text-xs font-bold mb-3 uppercase flex items-center gap-2">
                <span className="text-sm">⚡</span> Triggers
              </h3>
              <div className="space-y-2">
                {stepLibrary.triggers.map((step) => (
                  <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#325567] bg-[#192b33] cursor-grab hover:border-primary transition-colors">
                    <span className="text-xl">{step.icon}</span>
                    <p className="text-white text-sm font-medium">{step.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div>
              <h3 className="text-[#92b7c9] text-xs font-bold mb-3 uppercase flex items-center gap-2">
                <span className="text-sm">▶️</span> Actions
              </h3>
              <div className="space-y-2">
                {stepLibrary.actions.map((step) => (
                  <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#325567] bg-[#192b33] cursor-grab hover:border-primary transition-colors">
                    <span className="text-xl">{step.icon}</span>
                    <p className="text-white text-sm font-medium">{step.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Logic */}
            <div>
              <h3 className="text-[#92b7c9] text-xs font-bold mb-3 uppercase flex items-center gap-2">
                <span className="text-sm">🌳</span> Logic
              </h3>
              <div className="space-y-2">
                {stepLibrary.logic.map((step) => (
                  <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#325567] bg-[#192b33] cursor-grab hover:border-primary transition-colors">
                    <span className="text-xl">{step.icon}</span>
                    <p className="text-white text-sm font-medium">{step.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Center: Workflow Canvas */}
        <section className="flex-1 relative bg-background-dark overflow-hidden flex flex-col" style={{backgroundImage: 'radial-gradient(circle, #233c48 1px, transparent 1px)', backgroundSize: '30px 30px'}}>
          {/* Breadcrumbs / Toolbar Overlay */}
          <div className="absolute top-4 left-4 right-4 flex justify-between items-center pointer-events-none">
            <div className="flex items-center gap-2 bg-[#192b33]/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-[#325567] pointer-events-auto">
              <a className="text-[#92b7c9] text-xs font-medium hover:text-white" href="#">Workflows</a>
              <span className="text-[#92b7c9] text-xs">/</span>
              <span className="text-white text-xs font-bold">New Customer Onboarding</span>
            </div>
            <div className="flex gap-2 bg-[#192b33]/80 backdrop-blur-sm p-1 rounded-lg border border-[#325567] pointer-events-auto">
              <button className="p-2 text-white hover:bg-primary/20 rounded-md">
                <span className="text-lg">🎯</span>
              </button>
              <button className="p-2 text-white hover:bg-primary/20 rounded-md">
                <span className="text-lg">✋</span>
              </button>
              <div className="w-[1px] bg-[#325567] mx-1"></div>
              <button className="p-2 text-white hover:bg-primary/20 rounded-md">
                <span className="text-lg">🔍</span>
              </button>
              <button className="p-2 text-white hover:bg-primary/20 rounded-md">
                <span className="text-lg">🔍</span>
              </button>
            </div>
          </div>

          {/* Workflow Canvas Content */}
          <div className="flex-1 flex items-center justify-center p-20 relative">
            {/* Connection Lines */}
            <svg className="absolute inset-0 pointer-events-none w-full h-full">
              <path d="M 400 300 L 550 300" fill="none" stroke="#13a4ec" strokeDasharray="4" strokeWidth="2"></path>
              <path d="M 680 300 L 830 200" fill="none" stroke="#13a4ec" strokeDasharray="4" strokeWidth="2"></path>
              <path d="M 680 300 L 830 400" fill="none" stroke="#13a4ec" strokeDasharray="4" strokeWidth="2"></path>
            </svg>

            {/* Trigger Node */}
            <div className="absolute left-[250px] top-[260px] w-48 p-4 rounded-xl shadow-2xl flex flex-col gap-2 border-2 border-[#325567] bg-[#192b33]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-primary tracking-widest">Trigger</span>
                <span className="text-xs text-[#92b7c9]">⋯</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary text-xl">🔗</span>
                <p className="text-sm font-bold">Webhook Received</p>
              </div>
              <div className="mt-2 pt-2 border-t border-[#325567]">
                <p className="text-[10px] text-[#92b7c9]">Endpoint: /api/v1/new-user</p>
              </div>
              {/* Output Port */}
              <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
            </div>

            {/* Logic Node */}
            <div 
              className="absolute left-[530px] top-[250px] w-52 p-4 rounded-xl shadow-2xl flex flex-col gap-2 border-2 border-primary bg-[#192b33] cursor-pointer"
              onClick={() => setSelectedNode('condition')}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-[#92b7c9] tracking-widest">Logic</span>
                <span className="text-xs text-[#92b7c9]">⋯</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white text-xl">🔀</span>
                <p className="text-sm font-bold">Is User Premium?</p>
              </div>
              {/* Input Port */}
              <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
              {/* Output Ports */}
              <div className="absolute -right-2 top-1/4 -translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
              <div className="absolute -right-2 bottom-1/4 translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
            </div>

            {/* Action Node A */}
            <div className="absolute left-[810px] top-[140px] w-48 p-4 rounded-xl shadow-2xl flex flex-col gap-2 opacity-60 border-2 border-[#325567] bg-[#192b33]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-[#92b7c9] tracking-widest">Action</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white text-xl">🤖</span>
                <p className="text-sm font-bold">Priority Welcome</p>
              </div>
              <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
            </div>

            {/* Action Node B */}
            <div className="absolute left-[810px] top-[360px] w-48 p-4 rounded-xl shadow-2xl flex flex-col gap-2 opacity-60 border-2 border-[#325567] bg-[#192b33]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-[#92b7c9] tracking-widest">Action</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white text-xl">📧</span>
                <p className="text-sm font-bold">Standard Welcome</p>
              </div>
              <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background-dark"></div>
            </div>

            {/* Floating Zoom Controls */}
            <div className="absolute bottom-6 right-6 flex items-center gap-4 bg-[#192b33]/90 border border-[#325567] px-4 py-2 rounded-full">
              <span className="text-xs text-[#92b7c9] font-mono">100%</span>
              <div className="flex gap-2">
                <button className="text-[#92b7c9] hover:text-white">
                  <span className="text-xl">🎯</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Right Sidebar: Configuration Panel */}
        <aside className="w-80 border-l border-[#233c48] bg-background-light dark:bg-background-dark flex flex-col shrink-0">
          <div className="p-4 border-b border-[#233c48] flex justify-between items-center bg-[#192b33]">
            <div>
              <h2 className="text-white text-sm font-bold">Step Configuration</h2>
              <p className="text-[#92b7c9] text-xs">Logic: Condition (If/Else)</p>
            </div>
            <button className="text-[#92b7c9] hover:text-white">
              <span className="text-xl">✕</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Step Name */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#92b7c9] uppercase tracking-wider">Step Name</label>
              <input 
                className="w-full h-10 bg-[#192b33] border border-[#325567] rounded-lg px-3 text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none text-white" 
                type="text" 
                value={stepName}
                onChange={(e) => setStepName(e.target.value)}
              />
            </div>

            {/* Logic Builder Area */}
            <div className="space-y-4">
              <label className="text-xs font-bold text-[#92b7c9] uppercase tracking-wider">Conditions</label>
              <div className="p-3 bg-[#192b33] border border-[#325567] rounded-lg space-y-3">
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] text-[#92b7c9]">Variable</span>
                  <select className="w-full h-9 bg-[#111c22] border border-[#325567] rounded px-2 text-xs text-white">
                    <option>user.subscription_status</option>
                    <option>user.email</option>
                    <option>event.payload.price</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] text-[#92b7c9]">Operator</span>
                  <select className="w-full h-9 bg-[#111c22] border border-[#325567] rounded px-2 text-xs text-white">
                    <option>Equals</option>
                    <option>Contains</option>
                    <option>Starts With</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] text-[#92b7c9]">Value</span>
                  <input className="w-full h-9 bg-[#111c22] border border-[#325567] rounded px-2 text-xs text-white" type="text" defaultValue="premium"/>
                </div>
              </div>
              <button className="w-full py-2 border-2 border-dashed border-[#325567] rounded-lg text-xs text-[#92b7c9] hover:border-primary hover:text-primary transition-all">
                + Add Rule
              </button>
            </div>

            <div className="space-y-2 pt-4 border-t border-[#233c48]">
              <label className="text-xs font-bold text-[#92b7c9] uppercase tracking-wider">Outputs</label>
              <div className="flex items-center justify-between text-xs px-2 py-1 bg-[#192b33] rounded">
                <span>True</span>
                <span className="text-primary">Branch A</span>
              </div>
              <div className="flex items-center justify-between text-xs px-2 py-1 bg-[#192b33] rounded">
                <span>False</span>
                <span className="text-primary">Branch B</span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-[#233c48] bg-[#192b33]/50">
            <button className="w-full bg-primary text-white py-2 rounded-lg font-bold text-sm">Update Step</button>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default WorkflowBuilder;